import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { ArrowLeft, BookOpenCheck, Loader2, Play, RotateCcw } from "lucide-react";

const SECTION_LABELS = { cp: "C/P", bb: "B/B", ps: "P/S" };
const fmt = (iso) => new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric" });

function PassageCard({ p, selected, onToggle }) {
  const acc = p.accuracy == null ? null : Math.round(p.accuracy * 100);
  return (
    <button onClick={() => onToggle(p.passage_id)}
      className="pm-card p-4 text-left transition-transform hover:-translate-y-0.5 w-full"
      style={{ borderColor: selected ? "var(--primary)" : undefined, boxShadow: selected ? "0 0 0 2px var(--primary)" : undefined }}
      data-testid={`mp-card-${p.passage_id}`}>
      <div className="flex items-center gap-2 flex-wrap mb-1.5">
        <span className="pm-badge" style={{ background: "var(--muted)" }}>{SECTION_LABELS[p.section_id] || p.section_id.toUpperCase()}</span>
        <span className="pm-badge" style={{ background: "var(--muted)" }}>Type {p.passage_type}</span>
        <span className="text-[10px] font-mono" style={{ color: "var(--text-muted)" }}>{p.category_code}</span>
      </div>
      <div className="font-display font-bold text-base leading-snug">{p.title}</div>
      <div className="text-xs mt-1" style={{ color: "var(--text-secondary)" }}>{p.topic}{p.subtopic ? ` › ${p.subtopic}` : ""}</div>
      <div className="flex items-center justify-between mt-3 text-xs">
        <span style={{ color: "var(--text-muted)" }}>{p.question_count} Q · last seen {fmt(p.last_seen)}</span>
        {acc != null && <span className="font-mono" style={{ color: acc >= 80 ? "var(--mastery)" : acc >= 60 ? "var(--primary)" : "var(--weakness)" }}>{acc}%</span>}
      </div>
    </button>
  );
}

export default function MyPassages() {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("all");
  const [selected, setSelected] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/test-section/my-passages").then((r) => setData(r.data.passages)); }, []);

  const shown = useMemo(() => (data || []).filter((p) => filter === "all" || p.section_id === filter), [data, filter]);
  const toggle = (id) => setSelected((xs) => xs.includes(id) ? xs.filter((x) => x !== id) : [...xs, id].slice(-6));

  const replay = async () => {
    setBusy(true);
    try {
      const r = await api.post("/test-section/sessions/replay", { passage_ids: selected });
      nav(`/app/practice-test/session/${r.data.id}`);
    } finally { setBusy(false); }
  };

  if (!data) return <div className="py-24 text-center" data-testid="mp-loading"><Loader2 className="animate-spin mx-auto" /></div>;
  const facets = [["all", "All sections"], ["cp", "C/P"], ["bb", "B/B"], ["ps", "P/S"]];
  return (
    <div className="max-w-5xl mx-auto" data-testid="my-passages-page">
      <Link to="/app/practice-test" className="text-xs inline-flex items-center gap-1 hover:underline mb-3" style={{ color: "var(--primary)" }} data-testid="mp-back"><ArrowLeft size={12} /> Test Section</Link>
      <h1 className="font-display text-4xl font-bold mb-1 flex items-center gap-2"><BookOpenCheck size={28} /> My passages</h1>
      <p className="text-sm mb-6" style={{ color: "var(--text-secondary)" }}>
        Every science passage you've already worked on. Each one is locked — the questions, choices and figures are the exact set you were given. Pick up to 6 and replay them in one session, free.
      </p>
      <div className="flex items-center justify-between flex-wrap gap-2 mb-5">
        <div className="flex gap-2 flex-wrap" data-testid="mp-filters">
          {facets.map(([id, label]) => (
            <button key={id} onClick={() => setFilter(id)} className="pm-badge"
              style={{ background: filter === id ? "var(--primary)" : "var(--muted)", color: filter === id ? "white" : "var(--text-secondary)" }}
              data-testid={`mp-filter-${id}`}>{label}</button>
          ))}
        </div>
        {selected.length > 0 && (
          <button onClick={replay} disabled={busy} className="pm-btn-primary inline-flex items-center gap-2" data-testid="mp-replay-btn">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <RotateCcw size={14} />} Replay {selected.length} passage{selected.length > 1 ? "s" : ""}
          </button>
        )}
      </div>
      {shown.length === 0 ? (
        <div className="pm-card p-10 text-center" data-testid="mp-empty">
          <p className="font-display text-lg font-bold mb-2">Nothing saved here yet.</p>
          <p className="text-sm mb-5" style={{ color: "var(--text-secondary)" }}>Finish a Test Section passage and it'll show up here, locked to its questions, ready to replay anytime.</p>
          <Link to="/app/practice-test" className="pm-btn-primary inline-flex items-center gap-2" data-testid="mp-start-link"><Play size={14} /> Start a session</Link>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {shown.map((p) => <PassageCard key={p.passage_id} p={p} selected={selected.includes(p.passage_id)} onToggle={toggle} />)}
        </div>
      )}
    </div>
  );
}
